import { useEffect, useState } from 'react';
import {
  createNotepadEntry,
  listNotepadEntries,
  type NotepadEntry,
} from '../lib/notepad';
import { isSpeechSupported, useSpeechRecognition } from '../lib/voice';

interface Props {
  jobId: string;
  jobName?: string;
  onClose: () => void;
  onCountChange?: (count: number) => void;
  headerTitle?: string;
  /** Optional slot at the bottom (e.g., "Generate summary" button on the full page). */
  footer?: React.ReactNode;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Shared notepad UI used both by the full-page screen and the mid-shoot bottom sheet.
 * Entries persist to the server via /api/field/notepad/entries on Add.
 */
export function NotepadPanel({
  jobId,
  jobName,
  onClose,
  onCountChange,
  headerTitle = 'JOB NOTEPAD',
  footer,
}: Props) {
  const [entries, setEntries] = useState<NotepadEntry[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [photoRefOn, setPhotoRefOn] = useState(false);
  const [photoRef, setPhotoRef] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listNotepadEntries(jobId);
        if (cancelled) return;
        setEntries(rows);
        onCountChange?.(rows.length);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId, onCountChange]);

  function appendVoice(text: string) {
    setDraft((prev) => (prev ? `${prev} ${text}` : text));
  }
  const speechSupported = isSpeechSupported();
  const speech = useSpeechRecognition(appendVoice);

  async function addEntry() {
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);
    setErr(null);
    try {
      const created = await createNotepadEntry({
        job_id: jobId,
        entry_text: text,
        photo_ref_sequence: photoRefOn && photoRef !== '' ? Number(photoRef) : null,
      });
      const next = [...entries, created];
      setEntries(next);
      onCountChange?.(next.length);
      setDraft('');
      setPhotoRef('');
      setPhotoRefOn(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex h-full flex-col bg-white text-black">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
            {headerTitle}
          </div>
          {jobName && <div className="truncate text-sm font-semibold">{jobName}</div>}
          <div className="text-xs text-gray-500">
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded-full bg-gray-100 px-3 py-1.5 text-sm"
        >
          Close
        </button>
      </div>

      {/* Input */}
      <div className="border-b border-gray-200 p-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder="New note…"
          className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-eva-teal"
        />
        {speech.interim && (
          <div className="mt-1 text-xs italic text-gray-500">{speech.interim}</div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {speechSupported && (
            <button
              onPointerDown={speech.start}
              onPointerUp={speech.stop}
              onPointerLeave={speech.stop}
              className={`rounded-full px-3 py-1.5 text-sm ${
                speech.isRecording
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {speech.isRecording ? 'Listening…' : 'Hold to talk'}
            </button>
          )}
          <button
            onClick={() => setPhotoRefOn((v) => !v)}
            className={`rounded-full px-3 py-1.5 text-sm ${
              photoRefOn ? 'bg-eva-teal text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Ref photo
          </button>
          {photoRefOn && (
            <input
              type="number"
              min={1}
              value={photoRef}
              onChange={(e) =>
                setPhotoRef(e.target.value === '' ? '' : Number(e.target.value))
              }
              placeholder="#"
              className="w-16 rounded border border-gray-300 px-2 py-1 text-sm"
            />
          )}
          <button
            onClick={() => void addEntry()}
            disabled={!draft.trim() || saving}
            className="ml-auto rounded-lg bg-eva-teal px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Add'}
          </button>
        </div>
        {err && <div className="mt-2 text-xs text-red-600">{err}</div>}
      </div>

      {/* Entry log — newest first */}
      <div className="flex-1 overflow-y-auto">
        {entries.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No notes yet. Anything you jot here will feed the AI summary.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {[...entries]
              .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
              .map((e) => (
                <li key={e.id} className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-bracket-blue">
                      {formatTime(e.created_at)}
                    </span>
                    {e.photo_ref_sequence != null && (
                      <span className="rounded-full bg-bracket-amber/20 px-2 py-0.5 text-[10px] font-bold text-bracket-amber">
                        #{e.photo_ref_sequence}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap text-sm">
                    {e.entry_text}
                  </div>
                </li>
              ))}
          </ul>
        )}
      </div>

      {footer && (
        <div className="border-t border-gray-200 p-3">{footer}</div>
      )}
    </div>
  );
}
